import { useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "../client";
import type { CustomerProfileDto } from "./useCustomerProfile";

const PROVISION_KEY = ["customer-provision"] as const;

/**
 * Provisions the authenticated customer on login (get-or-create) and returns their profile.
 * Runs once per session behind the CustomerProvisionGate, before the app fans out any other data
 * request — so the customer always exists by the time those queries run. This replaces the old
 * just-in-time server-side creation and removes the first-sign-in race (one awaited call here
 * instead of N concurrent creates). react-query dedupes, so React StrictMode's double-mount in dev
 * does not fire two POSTs.
 */
export function useProvisionCustomer() {
  const queryClient = useQueryClient();

  return useQuery<CustomerProfileDto>({
    queryKey: PROVISION_KEY,
    queryFn: async () => {
      const res = await apiClient.post<CustomerProfileDto>("/customers/me/provision");
      // Seed the profile cache from the provision response (same shape) so the dashboard has the
      // customer immediately. Key must match PROFILE_KEY in useCustomerProfile.
      queryClient.setQueryData(["customer-profile"], res.data);
      return res.data;
    },
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 2,
  });
}
